/** Theme tokens for the chats admin viewer, scoped to `.chats-viewer-root`: the admin shell exposes its own, different token names, so the viewer ships a self-contained block. */

import { BRAND_PALETTE } from '@federa/const/brand-palette';
import { COLOR_RAMPS } from '@federa/const/color-ramps';
import { hexToRgba } from '@federa/const/hex-rgba';

/** PRE-GATE SHELL palette: the tab bar / gate notice render UPSTREAM of `.chats-viewer-root`, where neither the
 * viewer's scoped variables above nor the design system's cascade resolve, so these sites take LITERAL values from
 * this token leaf (the carve-out DS-HARDCODED-TW-COLOR-001's fix names for var()-unreachable contexts). The values
 * are the shell's long-rendered ones, so the cure changed no pixels; the two that ARE ramp rungs bind
 * `const/color-ramps.ts` instead of re-typing it (`DS-COLOR-RAMP-HEX-LITERAL-SSOT-001`), and `border` binds
 * `BRAND_PALETTE` (`DS-BRAND-HEX-LITERAL-SSOT-001`) since 2026-09-03 -- see the note at the member. What remains
 * literal here is `#ffffff` twice and one ink, none of which any colour SSOT carries. */
export const VIEWER_SHELL_COLORS = {
	muted: COLOR_RAMPS.neutral[500],
	// BOUND, not re-typed: this was a lower-cased transcription of `colors.border.muted`, which lived as a literal in
	// `brand/federa/tokens/colors.ts` and was therefore out of this layer's reach. The hue joined `BRAND_PALETTE` in the
	// same change, which is what makes the binding possible AND what puts a gate on it -- and NOT
	// `COLOR_RAMPS.neutral[200]`, which is `#E0E3E7`, a different colour two hex digits away.
	border: BRAND_PALETTE.borderMuted,
	surface: '#ffffff',
	/** Text on an accent-filled control (active tab, primary button, outbound bubble). */
	onAccent: '#ffffff',
	inkStrong: '#111827',
	ink: COLOR_RAMPS.neutral[700],
} as const;
/** The two values the block below declares as `--bg` / `--text` AND that `THEME_CSS` re-states as the root's own
 *  `background` / `color` at its tail. Bound rather than typed twice for the same reason the brand fallbacks below
 *  are: a hex spelled twice in one emitted string is a hex that can be half-changed. */
const VIEWER_ROOT_BG = '#f5f6f8';
const VIEWER_ROOT_TEXT = '#1d2027';

/** The active-participant tag's ink, carried by no colour SSOT: `COLOR_RAMPS.green[500]` is the emerald/mint ramp and
 *  `BRAND_PALETTE.primary` the forest-green brand line, and this is neither. It is NAMED rather than spelled at the
 *  member below because `--human-bubble-soft` is a tint OF it and must derive, not restate -- see that member. */
const HUMAN_BUBBLE = '#0e9f6e';
/** The tag fill's opacity over the ink above. Its own const so the two halves of the derivation read as one decision. */
const HUMAN_BUBBLE_SOFT_ALPHA = 0.12;

/**
 * The viewer's scoped custom properties: property NAME -> declared VALUE, in emission order.
 *
 * KEYED BY THE PROPERTY NAME so ONE spelling serves both halves of the round trip -- `THEME_CSS` declares from these
 * keys and `viewerVar` reads them back. The two halves DERIVE, they do not coincide: a `var(--text-faint)` in a view is
 * not a second decision that happens to match this block, it IS a read of this block, so renaming a property here must
 * move every reader or the reference resolves to nothing and the element renders unstyled with no console error and no
 * build failure -- the silent class `QUAL-UNDECLARED-CSS-VAR-REFERENCE-001` exists to catch, and whose docstring names
 * this viewer's references as the legitimate cohort it must not convict. Through the accessor a wrong name is a TYPE
 * error at the call site instead, which is the binding the raw `'var(--text-faint)'` string literals lacked.
 *
 * THE QUOTED KEYS ARE LOAD-BEARING, not style: `'--name':` is one of the declaration spellings that rule's corpus walk
 * admits, so these twenty names stay in its declared set exactly as the emitted `--name:` in the CSS string did.
 *
 * ALL THREE brand FALLBACKS are BOUND from `const/brand-palette.ts`, not re-typed: a `var()` fallback is a second
 * copy of the brand hex wherever it is spelled, and this leaf is exactly the var()-unreachable context in which that
 * copy is load-bearing -- which is why it must track the SSOT rather than a transcription of it. A transcription (a
 * lower-cased hex for `--primary-soft`, say) is a copy neither hex-SSOT rule can see unless the hue sits in
 * `COLOR_RAMPS` or `BRAND_PALETTE`, which is why `colors.brand.primaryLight` reads the palette: that is what makes the
 * binding possible AND what puts a gate on it. `--text-muted` is bound off `VIEWER_SHELL_COLORS.muted` above on the
 * same grounds: the pre-gate shell and the scoped block spell ONE muted ink, so a re-tint of the viewer moves all of it.
 *
 * `--human-bubble-soft` is the fill behind text already coloured `--human-bubble` (the active-participant tag;
 * `ParticipantsPanel` sets the two on one element). It is now DERIVED from that ink via `hexToRgba`, not declared
 * beside it: until 2026-09-04 it was the literal `rgba(14,159,110,0.12)` -- the SAME hue re-spelled in decimal
 * channels, which is a copy no hex-SSOT rule can see because adjacency is not a binding. This docstring asserted the
 * pair "moves together" and the block did not have that property: a re-tint of `--human-bubble` moved the tag's text
 * and left the fill behind it on the old green, the exact half-change the brand-fallback note two paragraphs up
 * forbids. The three channel/status tints are RUNGS of the shared ramps, bound rather than re-typed: a rung
 * spelled here as a literal is a second copy of a value `const/color-ramps.ts` owns, which is what
 * DS-COLOR-RAMP-HEX-LITERAL-SSOT-001 forbids.
 */
const VIEWER_VARS = {
	'--bg': VIEWER_ROOT_BG,
	'--surface': VIEWER_SHELL_COLORS.surface,
	'--surface-2': '#fafbfc',
	'--border': '#e6e8ec',
	'--text': VIEWER_ROOT_TEXT,
	'--text-muted': VIEWER_SHELL_COLORS.muted,
	'--text-faint': '#9aa1ad',
	'--primary': `var(--color-brand-primary,${BRAND_PALETTE.primary})`,
	'--primary-soft': `var(--color-brand-primary-light,${BRAND_PALETTE.primaryLight})`,
	'--user-bubble': '#eef0f3',
	'--ai-bubble': `var(--color-brand-primary,${BRAND_PALETTE.primary})`,
	'--human-bubble': HUMAN_BUBBLE,
	'--human-bubble-soft': hexToRgba(HUMAN_BUBBLE, HUMAN_BUBBLE_SOFT_ALPHA),
	'--sms': COLOR_RAMPS.yellow[500],
	'--web': COLOR_RAMPS.blue[500],
	'--danger': COLOR_RAMPS.red[500],
	'--radius': '14px',
	'--radius-sm': '9px',
	'--shadow': '0 1px 2px rgba(16,24,40,0.06),0 1px 3px rgba(16,24,40,0.1)',
	'--shadow-lg': '0 8px 24px rgba(16,24,40,0.12)',
} as const;

/** A `var()` read of one property of the block above -- the ONLY way a viewer surface may name one. The parameter type
 *  is the block's own key set, so a misspelled or retired name fails the build instead of rendering unstyled. */
export function viewerVar(name: keyof typeof VIEWER_VARS): string {
	return `var(${name})`;
}

export const THEME_CSS = `.chats-viewer-root{${Object.entries(VIEWER_VARS)
	.map(([name, value]) => `${name}:${value}`)
	.join(';')};color:${VIEWER_ROOT_TEXT};background:${VIEWER_ROOT_BG};}`;
